import { useState, useMemo } from "react";
import { useSearchParams } from "react-router";
import {
  ShieldAlert,
  BarChart3,
  Users,
  TrendingUp,
} from "lucide-react";
import {
  useCrmUserCount,
  useCrmPeriodStats,
  useCrmPeriodDetails,
  useMarketingReferrals,
} from "@/hooks/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toISODate } from "./shared/formatters";
import type { GroupedReferrals, PeriodStats } from "./shared/types";
import { PeriodSelector } from "./shared/period-selector";
import { PricingControls } from "./shared/pricing-card";
import { UserTable } from "./shared/user-table";
import { Dashboard } from "./dashboard";
import { Prognoser } from "./prognoser";

export function CrmPage() {
  const { hasRole } = useAuth();
  const { data: userCount } = useCrmUserCount();

  type CrmTab = "dashboard" | "kunder" | "prognoser";
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get("tab");
  const tab: CrmTab =
    rawTab === "kunder" || rawTab === "prognoser" ? rawTab : "dashboard";
  const setTab = (v: CrmTab) => setSearchParams({ tab: v }, { replace: true });

  // Period date range (default: MTD)
  const [periodFrom, setPeriodFrom] = useState(() => {
    const now = new Date();
    return toISODate(new Date(now.getFullYear(), now.getMonth(), 1));
  });
  const [periodTo, setPeriodTo] = useState(() => toISODate(new Date()));
  const [priceSource, setPriceSource] = useState("FURST_DISCOUNTED");
  // The assessment fee is new, so every historical figure moves when it is
  // charged. Off puts the page back on the numbers we have always reported.
  const [includeAssessment, setIncludeAssessment] = useState(true);
  // Revenue is retail-only, so compare it with retail lab figures by default.
  const [includeCompanyVisits, setIncludeCompanyVisits] = useState(false);

  // Forecasts and customers do not need the dashboard's expensive background queries.
  const dashboardEnabled = tab === "dashboard" && (hasRole("crm") || hasRole("superuser"));
  const { data: periodStats, isLoading: periodLoading } = useCrmPeriodStats(periodFrom, periodTo, priceSource, includeCompanyVisits, dashboardEnabled);
  const { data: periodDetails, isLoading: detailsLoading } = useCrmPeriodDetails(periodFrom, periodTo, priceSource, includeCompanyVisits, dashboardEnabled);
  const { data: marketingReferrals } = useMarketingReferrals(dashboardEnabled);

  // Group marketing referrals by source, filtered by period
  const groupedReferrals = useMemo<GroupedReferrals>(() => {
    if (!marketingReferrals) return {};
    const start = new Date(periodFrom + "T00:00:00");
    const end = new Date(periodTo + "T23:59:59.999");
    const groups: GroupedReferrals = {};

    const variantMap = new Map<string, number>();
    for (const ref of marketingReferrals) {
      if (!ref.createdAt) continue;
      const d = new Date(ref.createdAt);
      if (d < start || d > end) continue;

      let variant = ref.name || "unknown";
      if (ref.extraInfo) variant = `${variant}: ${ref.extraInfo}`;
      variantMap.set(variant, (variantMap.get(variant) ?? 0) + 1);
    }

    for (const [variant, count] of variantMap) {
      const parts = variant.split(": ");
      const baseName = parts.length > 1 ? parts[0]! : variant;
      if (!groups[baseName]) groups[baseName] = { count: 0, items: [] };
      const group = groups[baseName]!;
      group.count += count;
      if (parts.length > 1) {
        group.items.push({ label: parts.slice(1).join(": "), count });
      }
    }

    return groups;
  }, [marketingReferrals, periodFrom, periodTo]);

  const totalReferrals = useMemo(
    () => Object.values(groupedReferrals).reduce((sum, g) => sum + g.count, 0),
    [groupedReferrals],
  );

  if (!hasRole("crm") && !hasRole("superuser")) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <ShieldAlert className="h-12 w-12 text-admin-text-subtle" />
        <p className="text-lg text-admin-text-secondary">
          Du har ikke tilgang til CRM
        </p>
        <p className="text-sm text-admin-text-subtle">
          Kontakt en administrator for å få CRM-rollen.
        </p>
      </div>
    );
  }

  const layoutProps = {
    // Cast needed: generated type from OpenAPI client matches PeriodStats shape but isn't structurally identical
    periodStats: periodStats as unknown as PeriodStats | null,
    periodDetails: periodDetails ?? null,
    periodLoading,
    detailsLoading,
    groupedReferrals,
    totalReferrals,
    includeAssessment,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">CRM</h2>
          <p className="text-sm text-admin-text-subtle mt-0.5">
            {userCount ?? "–"} kunder totalt
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-lg bg-admin-card p-1">
          <Button
            variant={tab === "dashboard" ? "default" : "ghost"}
            size="sm"
            onClick={() => setTab("dashboard")}
          >
            <BarChart3 className="h-4 w-4 mr-1.5" />
            Dashboard
          </Button>
          <Button
            variant={tab === "kunder" ? "default" : "ghost"}
            size="sm"
            onClick={() => setTab("kunder")}
          >
            <Users className="h-4 w-4 mr-1.5" />
            Kunder
          </Button>
          <Button
            variant={tab === "prognoser" ? "default" : "ghost"}
            size="sm"
            onClick={() => setTab("prognoser")}
          >
            <TrendingUp className="h-4 w-4 mr-1.5" />
            Prognoser
          </Button>
        </div>
      </div>

      {tab === "dashboard" && (
        <>
          <div className="space-y-2">
            <PeriodSelector
              periodFrom={periodFrom}
              setPeriodFrom={setPeriodFrom}
              periodTo={periodTo}
              setPeriodTo={setPeriodTo}
            />
            <PricingControls
              priceSource={priceSource}
              setPriceSource={setPriceSource}
              includeAssessment={includeAssessment}
              setIncludeAssessment={setIncludeAssessment}
              includeCompanyVisits={includeCompanyVisits}
              setIncludeCompanyVisits={setIncludeCompanyVisits}
            />
          </div>
          <Dashboard {...layoutProps} />
        </>
      )}

      {tab === "kunder" && (
        <>
          <PricingControls
            priceSource={priceSource}
            setPriceSource={setPriceSource}
          />
          <UserTable priceSource={priceSource} />
        </>
      )}

      {tab === "prognoser" && <Prognoser />}
    </div>
  );
}
